import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationsProvider } from './context/NotificationsContext';
import Login from './pages/Login';
import DashboardLayout from './components/Layout';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import ContentPage from './pages/ContentPage';
import AddEditContentPage from './pages/AddEditContentPage';
import OracleCardsPage from './pages/OracleCardsPage';
import AddEditOracleCardPage from './pages/AddEditOracleCardPage';
import OracleReadingPage from './pages/OracleReadingPage';
import HomeImagesPage from './pages/HomeImagesPage';
import HomeSectionOrderPage from './pages/HomeSectionOrderPage';
import TagsPage from './pages/TagsPage';
import EventTypesPage from './pages/EventTypesPage';
import CalendarEventsPage from './pages/CalendarEventsPage';
import InvoicesPage from './pages/InvoicesPage';
import ProfilePage from './pages/ProfilePage';
import NotificationsPage from './pages/NotificationsPage';
import MonthlyThemePage from './pages/MonthlyThemePage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Import styles
import './assets/scss/themes.scss';
import './components/Layout/layout.scss';
import 'react-toastify/dist/ReactToastify.css';
import 'remixicon/fonts/remixicon.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <AuthProvider>
        <NotificationsProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="content" element={<ContentPage />} />
              <Route path="content/add" element={<AddEditContentPage />} />
              <Route path="content/edit/:id" element={<AddEditContentPage />} />
              <Route path="content/:categoryId" element={<ContentPage />} />
              <Route path="oracle-cards" element={<OracleCardsPage />} />
              <Route path="oracle-cards/add" element={<AddEditOracleCardPage />} />
              <Route path="oracle-cards/edit/:id" element={<AddEditOracleCardPage />} />
              <Route path="oracle-reading" element={<OracleReadingPage />} />
              <Route path="home-images" element={<HomeImagesPage />} />
              <Route path="home-section-order" element={<HomeSectionOrderPage />} />
              <Route path="diary/tags" element={<TagsPage />} />
              <Route path="diary/event-types" element={<EventTypesPage />} />
              <Route path="diary/calendar-events" element={<CalendarEventsPage />} />
              <Route path="invoices" element={<InvoicesPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="monthly-theme" element={<MonthlyThemePage />} />
            </Route>
            <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </BrowserRouter>
        </NotificationsProvider>
      </AuthProvider>
    </>
  );
}

export default App;
